import { createApiClient, createSlackApi, type SlackApi, type SlackIdentity } from '@teamofone/api-client'
import { dispatchUnauthorized } from './auth/unauthorized'
import { readTokens } from './auth/session'
import { getFreshAccessToken, type FreshTokenOptions } from './auth/tokenRefresh'

/**
 * Single shared SlackApi instance for the app. Base URL comes from
 * `VITE_TEAMOFONE_API_URL` (e.g. the cloudflared tunnel or staging BFF).
 * Dev identity is read from localStorage so a tester can flip between
 * `aditya` and a teammate without code changes.
 */
const baseUrl = (import.meta.env.VITE_TEAMOFONE_API_URL as string | undefined) ?? 'http://localhost:6055/teamofone'

// Token comes from the SSO storage (set on Authentik callback). Legacy
// `teamofone.jwt` key supported for read-only backwards compat with anyone
// holding an existing local session.
//
// VITE_TEAMOFONE_SEND_BEARER=false suppresses the Bearer header entirely so
// explicit dev-header auth can do its job. In SSO mode, bearer forwarding is
// on by default because the backend verifies Authentik JWTs.
const LEGACY_JWT_KEY = 'teamofone.jwt'
const DEFAULT_SOURCE_ID = '123'

function envValue(key: string): string | undefined {
  const viteEnv = import.meta.env as Record<string, string | undefined>
  const nodeEnv = (
    globalThis as typeof globalThis & {
      process?: { env?: Record<string, string | undefined> }
    }
  ).process?.env
  return nodeEnv?.[key] ?? viteEnv[key]
}

function shouldSendBearer(): boolean {
  const configured = envValue('VITE_TEAMOFONE_SEND_BEARER')
  if (configured === 'true') return true
  if (configured === 'false') return false
  return (envValue('VITE_TEAMOFONE_AUTH_MODE') ?? 'sso') !== 'dev'
}

function shouldSendDevHeaders(): boolean {
  if ((envValue('VITE_TEAMOFONE_AUTH_MODE') ?? 'sso') === 'dev') return true
  const apiUrl = envValue('VITE_TEAMOFONE_API_URL') ?? ''
  return (
    apiUrl === '/teamofone' ||
    apiUrl.startsWith('http://localhost:') ||
    apiUrl.startsWith('http://127.0.0.1:')
  )
}

function configuredSourceId(): string | null {
  const raw = envValue('VITE_TEAMOFONE_SOURCE_ID') ?? DEFAULT_SOURCE_ID
  const trimmed = raw.trim()
  return trimmed.length > 0 ? trimmed : null
}

function readToken(): string | null {
  if (!shouldSendBearer()) return null
  const tokens = readTokens()
  if (tokens?.accessToken) return tokens.accessToken
  if (typeof localStorage === 'undefined') return null
  return localStorage.getItem(LEGACY_JWT_KEY)
}

const client = createApiClient({
  baseUrl,
  getToken: currentJwtAsync,
  getExtraHeaders: currentApiHeaders,
  onUnauthorized: dispatchUnauthorized,
})

export const slackApi: SlackApi = createSlackApi(client)

const USER_KEY = 'teamofone.devUserId'
const LOC_KEY = 'teamofone.devLocationId'
const CURRENT_TEAM_KEY = 'teamofone.currentTeamId'
const LEGACY_TEAM_KEY = 'hl.teamId'
export const TEAM_CONTEXT_HEADER = 'x-teamofone-team-id'

function selectedTeamId(): string | null {
  if (typeof localStorage === 'undefined') return null
  return localStorage.getItem(CURRENT_TEAM_KEY) || localStorage.getItem(LEGACY_TEAM_KEY)
}

export function currentDevIdentity(): SlackIdentity {
  const userId = (typeof localStorage !== 'undefined' && localStorage.getItem(USER_KEY)) || 'dev-user'
  const selected = selectedTeamId()
  if (
    selected &&
    typeof localStorage !== 'undefined' &&
    localStorage.getItem(LOC_KEY) !== selected
  ) {
    localStorage.setItem(LOC_KEY, selected)
  }
  const locationId =
    selected ||
    (typeof localStorage !== 'undefined' && localStorage.getItem(LOC_KEY)) ||
    'loc-dev'
  return { userId, locationId }
}

export function currentTeamContextHeaders(): Record<string, string> {
  const selected = selectedTeamId()
  return selected ? { [TEAM_CONTEXT_HEADER]: selected } : {}
}

export function currentDevIdentityHeaders(): Record<string, string> {
  if (!shouldSendDevHeaders()) return {}
  const id = currentDevIdentity()
  return { 'x-user-id': id.userId, 'x-location-id': id.locationId }
}

export function currentApiHeaders(): Record<string, string> {
  const headers: Record<string, string> = {}
  const sourceId = configuredSourceId()
  if (sourceId) headers['source-id'] = sourceId
  return { ...headers, ...currentTeamContextHeaders(), ...currentDevIdentityHeaders() }
}

export function setDevIdentity(id: SlackIdentity): void {
  localStorage.setItem(USER_KEY, id.userId)
  localStorage.setItem(LOC_KEY, id.locationId)
}

/**
 * @deprecated tokens are now managed by `services/auth/session.ts`. Kept
 * for tests and the few API clients that still pass `currentJwt`.
 */
export function setJwt(token: string | null): void {
  if (typeof localStorage === 'undefined') return
  if (token) localStorage.setItem(LEGACY_JWT_KEY, token)
  else localStorage.removeItem(LEGACY_JWT_KEY)
}

export function currentJwt(): string | null {
  return readToken()
}

export async function currentJwtAsync(
  options: FreshTokenOptions = {},
): Promise<string | null> {
  if (!shouldSendBearer()) return null
  const token = await getFreshAccessToken(options)
  if (token) return token
  if (typeof localStorage === 'undefined') return null
  return localStorage.getItem(LEGACY_JWT_KEY)
}
